import { computed, effect, signal } from "@/react/core/reactive.ts";
import MenuOrder from "@kiosk-menu-order/MenuOrder/MenuOrder.tsx";
import MenuOrderCart from "@kiosk-menu-order/MenuOrderCart/MenuOrderCart.tsx";
import { makeTaxCategoriesAvailable } from "@/data/TaxCategoryHub.ts";
import { makeHappyHoursAvailable } from "@/data/HappyHourHub.ts";
import { getMaxIdZ, makeMaxIdsAvailable } from "@/data/MaxIdHub.ts";
import { onEnter, PosScreen, router } from "@/pos/PosRouter.ts";
import {
  calculateProductPrice,
  itemContext,
  layoutCategories0,
  orderLayout0,
  productLayouts,
  setV
} from "@/react/OrderView/OrderView.tsx";
import { makeGroupPrintersAvailable } from "@/data/GroupPrinterHub.ts";
import { makeDiscountsAvailable } from "@/data/DiscountHub.ts";
import { makePaymentsAvailable } from "@/data/PaymentHub.ts";
import { makeModifiersAvailable } from "@/data/ModifierHub.ts";
import { makeVouchersAvailable } from "@/data/VoucherHub.ts";
import debug from "debug";
import { createOrder, stripOrder } from "@/pos/logic/order-reactive.ts";
import _ from "lodash";
import { order0, setOrder0 } from "@/react/OrderView/OrderViewShare.ts";
import { CommitAction, MarketPlaceProvider, MasterHandlerCommand, OrderStatus, OrderType } from "@/pos/OrderType.ts";
import uuid from "time-uuid";
import { displayVFD } from "@/shared/vfd/vfd-init.ts";
import { type Product, ProductStatusKiosk } from "@/data/Product.ts";
import { orderPricingLevelFactory } from "@/react/OrderView/OrderPricingLevel.logic.ts";
import type { Order } from "@/data/Order.ts";
import { ItemFactoryContext } from "@/react/PaymentView/OrderItemsFactory2.tsx";
import { makeOnlineProviderAvailable, onlineProviders0 } from "@/data/OnlineProviderHub.ts";
import { makeMenusAvailable, menus0 } from "@/data/MenuHub";
import { categories0, makeCategoriesAvailable } from "@/data/CategoryHub.ts";
import type { Category } from "@/data/Category.ts";
import { HANDLERS } from "@/react/Dashboard.logic.ts";
import type { ModifierItem } from "@/data/Modifier.ts";
import { getProductImage, makeImagesAvailable } from "@/data/ImageHub.ts";
import { userFLow } from "@/shared/logger.ts";
import { loginUser } from "@/data/UserSignal.ts";
import { ensureValidItemNameLength } from "@/srm/lib/utils.ts";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getTicketNumber } from "@/react/Printer/print-label.ts";
import { runMasterHandlerPaidOrderFactory } from "@/data/OrderHub.ts";
import { OnlineOrder } from "@/data/OnlineOrder.ts";
import { clone } from '@atc-group/json-fn';
import { makeProductsAvailable, products0 } from "@/data/ProductHub.ts";

export enum KioskOrderMode {
  MENU_ORDER,
  CART_ORDER
}

export enum FilterMenuItem {
  All = 'All Items',
  Recommend = 'Recommend',
  BestSeller = 'Best Seller',
  New = 'New',
}

const log = debug('view:kiosk-order')

export const [selectedModifier, setSelectedModifier] = signal<ModifierItem[]>([]);
export const [currentOrderId, setCurrentOrderId] = signal<number | string>('');

//fixme: mock -> check
export const currentProvider = computed(() => onlineProviders0()[0])
export const currentMenu = computed(() => menus0().find(m => m.providers?.includes(currentProvider()?._id)))
export const categories1 = computed(() => categories0().filter(c => c.menus?.includes(currentMenu()?._id || ''))?.sort((a, b) => (a.position || 0) - (b.position || 0)))
export const [currentProductFilter, setCurrentProductFilter] = signal<Product>(undefined)
export const [currentFilterMode, setCurrentFilterMode] = signal<FilterMenuItem>(FilterMenuItem.All)
export const [kioskOrderMode, setKioskOrderMode] = signal<KioskOrderMode>(KioskOrderMode.MENU_ORDER);
export const [selectedCategoryId, setSelectedCategoryId] = signal<string>('')
export const selectedCategory = computed<Category | undefined>(() => {
  const categories = categories0()?.filter(c => c.menus?.includes(currentMenu()?._id || ''))
  if (!selectedCategoryId()) {
    return categories?.[0]
  }
  return categories?.find(c => c._id === selectedCategoryId())
})

effect(()=>{
  HANDLERS.selectMenu(currentMenu()?._id || '')
})
export const formatTimeToA = (time: string) => {
  if (!time) time = '06:00'
  const [hour, minute] = time.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${formattedHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
}

export const highlightText = (text: string, term: string) => {
  const parts = text?.split(new RegExp(`(${term})`, "gi")) || [];
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <strong key={index} className="font-bold text-[#ff852c]">
            {part}
          </strong>
        ) : (
          part
        )
      )}
    </>
  );
};

export const onConfirmKioskOrder = async () => {
  if (!ensureValidItemNameLength(order0()?.items)) {
    toast.error('Item with name or modifier name less than 2 digit is not allowed!!!')
    return
  }
  order0().commits?.push({ action: CommitAction.PRINT });
  getTicketNumber(order0())
  if (!order0().id) {
    const { id } = await getMaxIdZ(dayjs.unix(order0().vDate!))
    setOrder0({ ...order0(), id })
    setCurrentOrderId(id)
  } else {
    setCurrentOrderId(order0().id!)
  }
  const order = stripOrder(order0());
  order.type = OrderType.Kiosk
  order.status = OrderStatus.IN_PROGRESS;
  await OnlineOrder.upsert(clone(order))
}

export const getImageProduct = (productId?: string) => {
  const defaultImage = '/default-product-img.png'
  const product = products0().find(p => p._id === productId)
  const image = getProductImage(product?.imageId)?.data
  if (image) return image
  return defaultImage
}

export const quantityItemInOrder = (order: Order, itemId: string) => {
  if (!order.items?.length) return 0
  return order.items.find((it) => it.quantity > 0 && it.originalProductId === itemId)?.quantity || 0;
}

export const productsFilter = computed<Product[]>(() => {
  // fixme: for menu dine-in
  // const productLayoutsByCategory = productLayouts()?.filter(pl => pl.category === selectedCategory()?._id)
  // const _products = productLayoutsByCategory.map(layout =>
  //   products().find(product => product._id === layout.product && !product.menus?.length)
  // ).filter(Boolean) as Product[];

  // fixme: for menu online
  if (!products0()?.length) return []
  const _products = products0().filter(p => p.categories?.includes(selectedCategory()?._id || ''))
  if (!_products?.length) return []

  //fixme: mock -> check later
  const recommendProducts = _products.filter(p => p.status === ProductStatusKiosk.Recommend)
  const bestSellerProducts = _products.filter(p => p.status === ProductStatusKiosk.BestSeller)
  const newProducts = _products.filter(p => p.status === ProductStatusKiosk.New)

  switch (currentFilterMode()) {
    case FilterMenuItem.Recommend:
      return recommendProducts;
    case FilterMenuItem.BestSeller:
      return bestSellerProducts;
    case FilterMenuItem.New:
      return newProducts;
    default:
      return _products;
  }
})

export const goToOrderScreen = (mode: 'take-away' | 'dine-in' = 'take-away') => {
  const order = createOrder()
  if (mode === 'dine-in') {
    _.assign(order, { table: 'kiosk', takeAway: false })
  } else {
    _.assign(order, { takeAway: true })
  }
  setOrder0(order)
  router.screen = PosScreen.KIOSK_ORDER_VIEW
  setKioskOrderMode(KioskOrderMode.MENU_ORDER)
}

export const handleAddOnlyOneModifier = (newModifier: ModifierItem, product: Product, isEdit?: boolean) => {
  const updatedModifier = { ...newModifier, type: 'onlyOne' };

  let currentItem = order0().items.find((i) =>
    isEdit ? i._id === product._id : i.originalProductId === product._id && i.quantity > 0
  );

  let currentModifier = currentItem?.modifiers.find(i => i.originalModifierId === newModifier._id && i.quantity > 0);

  let modifierDelete = currentItem?.modifiers.find(i => i.type === updatedModifier.type && i.quantity > 0);

  if (modifierDelete) {
    order0().commits?.push({
      action: CommitAction.DELETE_MODIFIER_QUANTITY,
      commitId: currentItem?._id!,
      modifierId: modifierDelete?.originalModifierId!
    })
  }

  if (!currentModifier) {
    order0().commits?.push({
      action: CommitAction.ADD_MODIFIER,
      quantity: 1,
      ref: updatedModifier._id,
      _id: uuid(),
      productRef: { name: updatedModifier?.name!, price: updatedModifier?.price!, type: updatedModifier?.type },
      commitId: currentItem?._id!,
      ...itemContext.addSeat()
    })
  }
  ;
}

export const handleAddMultipleModifiers = (newModifier: ModifierItem, product: Product, isEdit?: boolean) => {
  const updatedModifier = { ...newModifier,type: 'multiple' };

  let currentItem = order0().items.find((i) =>
    isEdit ? i._id === product._id : i.originalProductId === product._id && i.quantity > 0
  );
  let currentModifier = currentItem?.modifiers.find(i => i.originalModifierId === updatedModifier._id && i.quantity > 0);

  if (!currentModifier) {
    order0().commits?.push({
      action: CommitAction.ADD_MODIFIER,
      quantity: 1,
      ref: updatedModifier._id,
      _id: uuid(),
      productRef: {name: updatedModifier?.name!, price: updatedModifier?.price!, type: updatedModifier?.type },
      commitId: currentItem?._id!,
      ...itemContext.addSeat()
    });
  } else {
    order0().commits?.push({
      action: CommitAction.DELETE_MODIFIER_QUANTITY,
      commitId: currentItem?._id!,
      modifierId: updatedModifier?._id!
    })
  }
};

export const handleAddModifiers = (newModifier: ModifierItem,type: string, product: Product, isEdit?: boolean) => {
  const updatedModifier = { ...newModifier,type: type };

  let currentItem = order0().items.find((i) =>
    isEdit ? i._id === product._id : i.originalProductId === product._id && i.quantity > 0
  );
  let currentModifier = currentItem?.modifiers.find(i => i.originalModifierId === newModifier._id && i.quantity > 0);
  if (!currentModifier) {
    order0().commits?.push({
      action: CommitAction.ADD_MODIFIER,
      quantity: 1,
      ref: updatedModifier._id,
      _id: uuid(),
      productRef: {name: updatedModifier?.name!, price: updatedModifier?.price!, type: updatedModifier?.type },
      commitId: currentItem?._id!,
      ...itemContext.addSeat()
    });
  }
};

export const onAddProduct = (product: Product, quantity = 1) => {
  const { getMultipleTaxes, getMultipleTaxesComponent } = orderPricingLevelFactory()
  order0().commits?.push({
    action: CommitAction.ADD_PRODUCT,
    ref: product!._id,
    _id: uuid(),
    productRef: {
      id: product?.id,
      name: product?.name!,
      price: calculateProductPrice(product),
      taxes: getMultipleTaxes(product)(),
      ingredients: product?.ingredients,
      ...(product?.categories && product?.categories!.length > 0 && { categories: product?.categories }),
      //fixme taxComponents later
      taxComponents: getMultipleTaxesComponent(product, 1)() /*product.taxComponents*/,
      taxComponents2: getMultipleTaxesComponent(product, 2)() /*product.taxComponents2*/,
    },
    note: product?.note,
    ...itemContext.addSeat(),
    groupPrinter: product!.groupPrinter,
    groupPrinter2: product!.groupPrinter2,
    labelPrinter: product?.labelPrinter,
    quantity: quantity ?? 1,
  })
  displayVFD({
    sum: order0().vTotal!.toString(),
    discount: (order0().discount || 0).toString(),
    item: _.last(order0().items)!,
  }).then()
}

//@ts-ignore
window.products = products0
//@ts-ignore
window.productsFilter = productsFilter
//@ts-ignore
window.layoutCategories0 = layoutCategories0
//@ts-ignore
window.orderLayout0 = orderLayout0
//@ts-ignore
window.productLayouts = productLayouts
//@ts-ignore
window.selectedCategory = selectedCategory
//@ts-ignore
window.currentMenu = currentMenu
//@ts-ignore
window.currentProvider = currentProvider
//@ts-ignore
window.categories1 = categories1

export const onToggleTakeAway = () => {
  userFLow(`toggle take away`, {
    orderId: order0()?._id,
    username: loginUser()?.name
  });
  order0().commits?.push({
    action: CommitAction.CHANGE_ORDER_TAKE_AWAY_KIOSK,
    takeAway: !order0().takeAway || false,
  })
}
const KioskOrderView = () => {
  makeTaxCategoriesAvailable()
  makeHappyHoursAvailable()
  makeMaxIdsAvailable().then()
  makeOnlineProviderAvailable()
  makeMenusAvailable()
  makeProductsAvailable() //fixme: use this for online-menu instead of dine-menu
  onEnter(PosScreen.KIOSK_ORDER_VIEW, async () => {
    // setV(prev => prev + 1) //fixme: uncomment when use dine-menu
  })
  makeGroupPrintersAvailable()
  makeDiscountsAvailable()
  makePaymentsAvailable()
  makeModifiersAvailable()
  makeVouchersAvailable()
  makeCategoriesAvailable()
  makeImagesAvailable()
  return (
    <ItemFactoryContext.Provider value={itemContext}>
      {
        kioskOrderMode() === KioskOrderMode.MENU_ORDER ?
          <MenuOrder />
          :
          <MenuOrderCart />
      }
    </ItemFactoryContext.Provider>
  )
}

export default KioskOrderView;